export const metricKeys = ['level', 'exp', 'hp', 'mp', 'mesos', 'mapName'] as const
export const recognitionMetricKeys = [...metricKeys, 'jobName'] as const

export type MetricKey = (typeof metricKeys)[number]
export type RecognitionMetricKey = (typeof recognitionMetricKeys)[number]
export const autoLocateMetricKeys = recognitionMetricKeys
export type MetricRecognitionIntervalMs = 0 | 250 | 500 | 1_000 | 2_000 | 3_000 | 4_000 | 5_000 | 10_000 | 30_000
export type MetricStrategy = 'bar' | 'ocr' | 'fused' | 'unavailable'
export type ReadingSource = 'bar' | 'ocr' | 'fused'
export type Availability = 'available' | 'unavailable'

export interface NormalizedRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface PixelRect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface MetricReading<T = number> {
  readonly value: T | null
  readonly confidence: number
  readonly source: ReadingSource
  readonly availability: Availability
  readonly rawText?: string
  readonly ocrValue?: T
  /** The recognizer has already confirmed this value across independent OCR samples. */
  readonly confirmation?: 'consensus'
  readonly capturedAt: number
}

export interface ResourceValue {
  readonly current: number | null
  readonly max: number | null
  readonly ratio: number | null
}

export interface ExpValue {
  readonly absolute: number | null
  readonly ratio: number | null
  readonly estimated?: boolean
  /** Session-local requirement inferred from exact EXP and percentage samples. */
  readonly requiredExp?: number | null
  /** The in-game percentage is rounded, so an inferred requirement is approximate. */
  readonly requiredExpApproximate?: boolean
  readonly requiredExpRange?: { readonly minimum: number; readonly maximum: number } | null
  readonly percentagePrecision?: number
}

/** One EXP change may be completed or corrected by later level evidence. */
export interface ExpChange {
  readonly id: string
  readonly revision: number
  readonly fromSourceAt: number
  readonly status: 'pending' | 'confirmed' | 'discarded'
  readonly kind?: 'level-up' | 'loss' | 'correction'
}

export interface ExpAdmission {
  readonly change?: ExpChange
  /** Level associated with an accepted EXP frame, which may predate the current HUD level. */
  readonly level?: number | null
  readonly confirmedLoss?: boolean
  readonly status: 'accepted' | 'pending' | 'rejected'
  readonly reason?: 'decrease' | 'requirement-conflict' | 'large-gain' | 'level-correction' | 'recovery' | 'invalid' | 'stale'
  readonly samples: number
  readonly value?: ExpValue
  /** First sample of a confirmed replacement baseline; its cross-baseline gap is never earned. */
  readonly rebaseline?: ExpExactEvidence
  readonly creditedDelta?: number
  readonly threshold?: number
  readonly transition?: 'baseline' | 'continuous' | 'level-up' | 'rebaseline'
  /** Monotonic admitted earnings, shared by candidates and delayed exact evidence. */
  readonly accounting?: {
    readonly total: number
    readonly epoch: number
    readonly sourceAt: number
    readonly epochStartTotal: number
    readonly epochStartAt: number
  }
}

/** A verified absolute EXP reading tied to the frame that OCR actually read. */
export interface ExpExactEvidence {
  readonly value: ExpValue
  readonly level: number | null
  /** Actual level OCR frame, never the time a cached level was delivered. */
  readonly levelSourceAt?: number | null
  readonly confidence: number
  /** Decimal places shown by the in-game percentage, used to model rounding. */
  readonly percentagePrecision?: number
  readonly sourceAt: number
  readonly deliveredAt: number
  readonly confirmation: 'source-frame' | 'consensus'
}

/** A complete EXP OCR result that is not yet trusted for accounting. */
export interface ExpExactCandidate {
  readonly value: ExpValue & { readonly absolute: number; readonly ratio: number }
  readonly level: number | null
  readonly levelSourceAt: number | null
  readonly confidence: number
  readonly percentagePrecision: number
  readonly sourceAt: number
  readonly deliveredAt: number
  readonly barConsistency: 'match' | 'conflict' | 'unavailable'
  readonly recognitionPath: 'legacy' | 'adaptive'
  readonly rawText?: string
}

export type MetricDiagnosticValue = number | string | ExpValue | ResourceValue

export interface MapSuffixDiagnostic {
  readonly state: 'not-applicable' | 'verified' | 'corrected' | 'ambiguous' | 'clipped'
  readonly kind?: 'arabic' | 'roman' | 'none' | 'unknown'
  readonly evidence?: 'pixels' | 'ocr' | 'none'
  readonly reason?: 'missing' | 'conflict' | 'unrecognized' | 'edge'
  readonly ocrConfidence?: number
  readonly bodyConfidence?: number
  readonly ocrSuffix?: string
  readonly suffix?: string
  readonly score?: number
  /** Pixel coordinates within the complete authorized map ROI. */
  readonly rect?: PixelRect
}

export interface MapNameConfirmationDiagnostic {
  readonly suffix?: MapSuffixDiagnostic
  readonly state: 'recognizing' | 'confirming' | 'ready' | 'uncertain'
  readonly candidateValue?: string
  readonly confirmedSamples: number
  readonly targetSamples: number
  readonly pendingSince?: number
  readonly mode?: 'cross-variant' | 'stable-variant' | 'suffix-frames' | 'independent-frames'
  readonly reason?: 'collecting' | 'conflict' | 'unstable' | 'expired' | 'suffix-ambiguous' | 'suffix-clipped'
  readonly expirationReason?: 'sample-gap' | 'window-limit' | 'cadence-changed'
  readonly sampleGapMs?: number
  readonly windowMs?: number
  readonly expiresAt?: number
}
export type MetricRecognitionStatus =
  | 'disabled'
  | 'queued'
  | 'running'
  | 'ready'
  | 'confirming'
  | 'source-hidden'
  | 'no-result'
  | 'no-text'
  | 'parse-failed'
  | 'low-confidence'

export type MetricRecognitionFailureReason =
  | 'layout-unavailable'
  | 'resource-bar-conflict'
  | 'no-text'
  | 'parse-failed'
  | 'low-confidence'

export type ExpConfirmationFailureReason = 'invalid-exp-range' | 'incompatible-exp-samples'

/** One enhancement of a source frame; never an independent confirmation vote. */
export interface OcrAttemptSummary {
  readonly enhancementIndex: number
  readonly rawText: string
  readonly confidence?: number
  readonly durationMs?: number
  readonly status: 'ready' | 'no-text' | 'parse-failed' | 'low-confidence'
}

/** One completed OCR attempt; independent of cached published readings. Runtime only. */
export interface OcrCompletionSnapshot {
  readonly rawText: string
  readonly value?: MetricDiagnosticValue
  readonly capturedAt: number
  readonly status: MetricRecognitionStatus
  readonly confidence?: number
  readonly minimumConfidence?: number
  readonly attempts?: readonly OcrAttemptSummary[]
  /** Owned pixels from this attempt, never a later live preview frame. Runtime only. */
  readonly input?: OcrInputSnapshot
}

export interface OcrInputSnapshot {
  readonly roi: ImageData
  readonly source: ImageData
  readonly processed: ImageData | null
  readonly sourceRect: NormalizedRect
  readonly method: 'luminance-smooth' | 'luminance-pixelated'
}

export interface MetricRecognitionDiagnostic {
  readonly completion?: OcrCompletionSnapshot
  readonly mapConfirmation?: MapNameConfirmationDiagnostic
  readonly expDisplay?: ExpDisplayUpdate
  readonly expAdmission?: ExpAdmission
  readonly status: MetricRecognitionStatus
  readonly value?: MetricDiagnosticValue
  /** Latest parseable value, which may still be awaiting confirmation. */
  readonly candidateValue?: MetricDiagnosticValue
  /** Last value published to the normal reading stream. */
  readonly acceptedValue?: MetricDiagnosticValue
  readonly confirmedSamples?: number
  readonly targetSamples?: number
  readonly pendingSince?: number
  readonly rawText?: string
  readonly confidence?: number
  readonly enhancementIndex?: number
  readonly lastAttemptAt?: number
  readonly durationMs?: number
  readonly failureReason?: MetricRecognitionFailureReason
  readonly confirmationFailureReason?: ExpConfirmationFailureReason
  /** Latest failed review attempt while a previously parsed candidate remains pending. */
  readonly reviewFailureReason?: MetricRecognitionFailureReason
  readonly selectedText?: string
  readonly roi?: AdaptiveMetricRoi
  readonly recognitionPath?: 'legacy' | 'adaptive'
  readonly capturedAt: number
}

export type AdaptiveRoiStatus = 'searching' | 'adapted' | 'fallback'
export type AdaptiveRoiSource = 'preset' | 'manual'
export type AdaptiveRoiMode = 'legacy' | 'candidate' | 'adaptive'
export type AdaptiveRoiFallbackReason = 'legacy-success' | 'adaptive-failed' | 'conflict'

/** Runtime-only recognition regions. Search ranges remain the only persisted ROI values. */
export interface AdaptiveMetricRoi {
  readonly searchRect: NormalizedRect
  readonly textRect: NormalizedRect
  readonly barRect?: NormalizedRect
  readonly effectiveRect: NormalizedRect
  readonly status: AdaptiveRoiStatus
  readonly mode: AdaptiveRoiMode
  readonly textMode: AdaptiveRoiMode
  readonly barMode: AdaptiveRoiMode
  readonly source: AdaptiveRoiSource
  readonly confidence: number
  readonly updatedAt: number | null
  readonly fallbackReason?: AdaptiveRoiFallbackReason
}

export interface RawReading {
  readonly capturedAt: number
  /** Runtime visibility evidence; numeric OCR keeps its own original source time. */
  readonly sourceObservation?: {
    readonly samplingIntervalMs: number
    readonly mesosVisibility: 'visible' | 'uncertain' | 'hidden'
  }
  /** Identifies one uninterrupted period in which the inventory is confirmed visible. */
  readonly mesosContinuityId?: number
  readonly level: MetricReading
  readonly exp: MetricReading<ExpValue>
  readonly expExactCandidate?: ExpExactCandidate
  readonly expExactEvidence?: ExpExactEvidence
  readonly hp: MetricReading<ResourceValue>
  readonly mp: MetricReading<ResourceValue>
  readonly mesos: MetricReading
  readonly mapName: MetricReading<string>
  readonly jobName: MetricReading<string>
  readonly diagnostics: {
    readonly hudConfidence: number
    readonly processingMs: number
    readonly rejectedReason?: string
    readonly metrics?: Readonly<Partial<Record<RecognitionMetricKey, MetricRecognitionDiagnostic>>>
  }
}

export interface ValidatedObservation extends RawReading {
  readonly expCandidateAdmission?: ExpAdmission
  readonly expEvidenceAdmission?: ExpAdmission
  readonly expQuarantined?: boolean
  readonly accepted: Readonly<Record<RecognitionMetricKey, boolean>>
  readonly expExactEvidenceAccepted?: boolean
  /** A confirmed same-level EXP decrease that must start a new accounting baseline. */
  readonly expRebaseline?: true
  /** A confirmed same-level decrease in the independently delivered exact endpoint. */
  readonly expExactRebaseline?: true
}

export interface GameStateSnapshot {
  readonly capturedAt: number
  readonly observedAt?: Readonly<Record<MetricKey, number | null>>
  readonly level: number | null
  readonly exp: ExpValue
  readonly hp: ResourceValue
  readonly mp: ResourceValue
  readonly mesos: number | null
  readonly mapName: string | null
  /** Optional so history records saved before job recognition remain compatible. */
  readonly jobName?: string | null
  readonly confidence: Readonly<Record<MetricKey, number>>
  readonly coverage: Readonly<Record<MetricKey, number>>
}

export type GameEventType =
  | 'level-up'
  | 'mesos-changed'
  | 'map-changed'
  | 'capture-interrupted'
  | 'capture-restored'

export interface GameEvent {
  readonly id: string
  readonly sessionId: string
  readonly type: GameEventType
  readonly capturedAt: number
  readonly amount?: number
  readonly from?: number
  readonly to?: number
  readonly previousMapName?: string
  readonly mapName?: string
  readonly confidence: number
}

export type SessionStatus = 'idle' | 'running' | 'paused' | 'ended' | 'interrupted'

export interface SessionAggregate {
  readonly durationMs: number
  /** Versions 7-8 use admitted positive earnings; version 9+ uses displayed net earnings across active segments. */
  readonly expGained: number | null
  readonly expDisplayStatus?: ExpDisplayUpdate['status']
  /** True when a cross-level endpoint difference uses a runtime-inferred requirement. */
  readonly expGainedApproximate?: boolean
  /** Trusted contiguous EXP used as the hourly-rate numerator. */
  readonly expRateGained?: number | null
  /** Trusted contiguous EXP observation time used by the hourly rate. */
  readonly expTrustedDurationMs?: number
  /** Internal trusted EXP rate reserved for future leaderboard submissions. */
  readonly expTrustedPerHour?: number | null
  /** User-facing accumulated EXP earnings divided by the active record duration. */
  readonly expPerHour: number | null
  /** Optional for records saved before session balance tracking was introduced. */
  readonly mesosStartBalance?: number | null
  /** Optional for records saved before session balance tracking was introduced. */
  readonly mesosCurrentBalance?: number | null
  /** Current confirmed balance minus the first confirmed balance in this record. */
  readonly mesosBalanceChange?: number | null
  readonly mesosDelta: number | null
  /** Trusted visible inventory time used by the Mesos hourly rate. */
  readonly mesosTrustedDurationMs?: number
  readonly mesosPerHour: number | null
  /** Legacy backup fields only. New records do not compute resource usage. */
  readonly hpConsumed?: number
  readonly hpConsumedPerHour?: number | null
  readonly hpRecovered?: number
  readonly hpRecoveredPerHour?: number | null
  readonly mpConsumed?: number
  readonly mpConsumedPerHour?: number | null
  readonly mpRecovered?: number
  readonly mpRecoveredPerHour?: number | null
  readonly coverage: Readonly<Record<MetricKey, number>>
}

export type LiveRateStatus = 'ready' | 'provisional' | 'unavailable'

export interface LiveRateMetric {
  readonly value: number | null
  readonly status: LiveRateStatus
  readonly updatedAt: number | null
}

export interface ActiveLiveRateMetric extends LiveRateMetric {
  readonly calculationStatus?: ExpDisplayUpdate['status']
  readonly approximate?: boolean
  readonly activeDurationMs: number
}

export interface TrustedLiveRateMetric extends LiveRateMetric {
  readonly trustedDurationMs: number
}

export interface LiveRateSnapshot {
  /** Runtime presentation only; never used for settlement or ranking. */
  readonly expReadout?: ExpReadoutSnapshot
  readonly expRecovery?: ExpRecoveryMetric
  /** Runtime-only, same net-EXP accounting as expPerHour. Missing in older widget snapshots. */
  readonly expRealtime?: RealtimeExpRateMetric
  /** User-facing endpoint gain divided by the displayed active record duration. */
  readonly expPerHour: ActiveLiveRateMetric
  /** Internal trusted rate reserved for future leaderboard submissions. */
  readonly expTrustedPerHour: TrustedLiveRateMetric
  readonly mesosPerHour: TrustedLiveRateMetric
}

export type ExpReadoutReason = 'checking-change' | 'waiting-level' | 'missing-levels' | 'source-unreadable' | 'confirmation-timeout' | 'continuity-break'
export interface ConfirmedExpReadout {
  readonly value: number
  readonly netGain: number
  readonly sourceAt: number
  readonly point: ExpPoint
}
export interface ExpReadoutSnapshot {
  readonly status: 'ready' | 'updating' | 'stale' | 'recovering'
  readonly scope: 'session' | 'recovery'
  readonly incomplete: boolean
  readonly reason: ExpReadoutReason | null
  readonly since: number | null
  readonly holdMs: number
  readonly lastConfirmed: ConfirmedExpReadout | null
}
export interface ExpRecoveryMetric extends ActiveLiveRateMetric {
  readonly startedAt: number | null
  readonly netGain: number | null
}

export interface RealtimeExpRateMetric {
  readonly value: number | null
  readonly delta: number | null
  readonly status: 'ready' | 'warming' | 'stale' | 'pending' | 'unavailable'
  readonly direction: 'up' | 'down' | null
  readonly windowMs: number
  readonly windowStartAt: number | null
  readonly updatedAt: number | null
  readonly approximate: boolean
}

export interface SessionRecord {
  readonly rankIneligibleReason?: 'confirmed-exp-loss' | 'low-performance-tier' | 'capture-not-window' | 'capture-title-mismatch'
  readonly id: string
  readonly presetId: string
  readonly presetVersion: number
  readonly profileId: string
  readonly status: SessionStatus
  readonly startedAt: number
  readonly endedAt?: number
  readonly activeDurationMs: number
  /** Version of the local session accounting semantics; absent on legacy records. */
  readonly sessionAccountingVersion?: number
  /** Version of the separately persisted leveling journey; absent on legacy records. */
  readonly journeyVersion?: number
  readonly pausedAt?: number
  readonly aggregate: SessionAggregate
  readonly startState?: GameStateSnapshot
  readonly endState?: GameStateSnapshot
}

export type AccountingContinuity = 'ignored' | 'baseline' | 'rebaseline' | 'contiguous' | 'discontinuous' | 'unavailable' | 'pending'

export interface AccountingMetricUpdate {
  readonly continuity: AccountingContinuity
  readonly sourceAt: number | null
  readonly trustedIntervalMs: number
}

export interface AccountingExpUpdate extends AccountingMetricUpdate {
  readonly delta: number | null
  readonly exact: boolean
  readonly start: ExpPoint | null
  readonly end: ExpPoint | null
  readonly sourceStartAt: number | null
  readonly sourceEndAt: number | null
  readonly activeStartAt: number | null
  readonly activeEndAt: number | null
}

export interface AccountingMesosUpdate extends AccountingMetricUpdate {
  readonly delta: number | null
  readonly startValue: number | null
  readonly endValue: number | null
}

export interface AccountingFrame {
  readonly capturedAt: number
  readonly captureGeneration: number
  readonly exp: AccountingExpUpdate
  readonly mesos: AccountingMesosUpdate
}

export interface SessionIngestResult {
  readonly record: SessionRecord
  readonly accounting: AccountingFrame
}

export interface CaptureProfile {
  /** Independent of preset releases: only a game UI change invalidates calibration. */
  readonly layoutVersion?: number
  readonly autoRois?: Partial<Record<RecognitionMetricKey, LocatedMetricRegion>>
  readonly id: string
  readonly name: string
  readonly presetId: string
  readonly presetVersion: number
  readonly sourceWidth: number
  readonly sourceHeight: number
  readonly hudRect: NormalizedRect
  readonly roiOverrides: Partial<Record<RecognitionMetricKey, NormalizedRect>>
  readonly colorCalibration: Partial<Record<'hp' | 'mp' | 'exp', readonly [number, number, number]>>
  readonly recognition: {
    readonly captureFps: number
    /** Legacy frequency multipliers. Read only while migrating older profiles. */
    readonly metricCaptureFps?: Readonly<Partial<Record<RecognitionMetricKey, number>>>
    /** Legacy frequency settings version. Read only while migrating older profiles. */
    readonly metricCaptureFpsVersion?: number
    readonly metricRecognitionIntervalsMs?: Readonly<Partial<Record<RecognitionMetricKey, MetricRecognitionIntervalMs>>>
    readonly metricRecognitionIntervalsVersion?: number
    readonly minConfidence: number
    readonly ocrChangeThreshold: number
  }
  readonly createdAt: number
  readonly updatedAt: number
}

export interface LocatedMetricRegion {
  /** Missing on existing profiles, which use the original wide HUD. */
  readonly hudVariant?: 'wide' | 'compact'
  readonly rect: NormalizedRect
  /** Compact OCR crop. Absent on profiles saved before text/bar separation. */
  readonly textRect?: NormalizedRect
  readonly barRect?: NormalizedRect
  readonly confidence: number
}

export interface AppSetting<T = unknown> {
  readonly key: string
  readonly value: T
  readonly updatedAt: number
}

export interface RankExpPoint {
  readonly level: number
  readonly absolute: number
}

export interface RankExpMetric {
  readonly gain: number
  readonly trustedDurationMs: number
  readonly start: RankExpPoint
  readonly end: RankExpPoint
}

export interface RankSubmissionDraft {
  readonly submissionId: string
  readonly presetId: 'mxdc'
  readonly presetVersion: number
  readonly mapName: string
  readonly levelBucket: number
  readonly jobKey?: string
  readonly jobName?: string
  readonly startedAt: number
  readonly endedAt: number
  readonly exp: RankExpMetric
}

export interface RankSubmission extends RankSubmissionDraft {
  readonly visitorId: string
}

export type RankQueueStatus = 'pending' | 'configuration-blocked'

export interface RankQueueItem {
  readonly id: string
  readonly sessionId: string
  readonly accountingVersion: number
  readonly submission: RankSubmissionDraft
  readonly attempts: number
  readonly createdAt: number
  readonly updatedAt: number
  readonly nextAttemptAt: number
  readonly status: RankQueueStatus
  readonly lastError?: string
}

export type RankSource = 'all' | 'mxdc' | 'fengji'
export type RankBrandSource = Exclude<RankSource, 'all'>
export const RANK_SOURCE_ORDER = ['all', 'fengji', 'mxdc'] as const satisfies readonly RankSource[]
export const RANK_BRAND_SOURCE_ORDER = ['fengji', 'mxdc'] as const satisfies readonly RankBrandSource[]

export function orderedRankSources(sources: readonly RankSource[]): RankSource[] {
  const rank = new Map<RankSource, number>(RANK_SOURCE_ORDER.map((source, index) => [source, index]))
  return [...new Set(sources)].sort((left, right) => (rank.get(left) ?? Number.POSITIVE_INFINITY) - (rank.get(right) ?? Number.POSITIVE_INFINITY))
}
export type RankLevelBucketFilter = number | 'all'
export type RankTab = 'formal' | 'accumulating'
export type RankStatus = 'all' | RankTab

export interface RankMetricSummary {
  readonly p25: number
  readonly p50: number
  readonly p75: number
  readonly p90: number
}

export function rankItemKey(item: Pick<RankMapItem, 'mapKey' | 'levelBucket' | 'jobKey'>): string {
  return `${item.mapKey}:${item.levelBucket}:${item.jobKey}`
}

export interface RankMapItem {
  readonly rank: number | null
  readonly presetId: 'mxdc'
  readonly mapName: string
  readonly mapKey: string
  readonly mapId: number | null
  readonly mapIcon: string
  readonly mapDetailUrl: string | null
  readonly levelBucket: number
  readonly metric: 'exp'
  readonly jobKey: string
  readonly userCount: number
  readonly totalTrustedDurationMs: number
  readonly percentiles: RankMetricSummary
  readonly sources: {
    readonly mxdc: number
    readonly fengji: number
  }
  readonly qualified: boolean
  readonly updatedAt: number
}

export interface RankMapPage {
  /** Optional for responses from older proxies. */
  readonly status?: RankStatus
  /** Echoed search keyword when the upstream applied a map query. */
  readonly q?: string
  readonly formal: {
    readonly items: readonly RankMapItem[]
    readonly page: number
    readonly pageSize: number
    readonly total: number
  }
  readonly accumulating: {
    readonly items: readonly RankMapItem[]
    /** Older proxies omit accumulating pagination metadata. */
    readonly page?: number
    readonly pageSize?: number
    readonly total: number
  }
  readonly generatedAt: number
}

export interface RankJobOption {
  readonly jobKey: string
  readonly jobName: string
}

export interface RankMetaJob {
  readonly key: string
  readonly name: string
  /** Null only while accepting the legacy metadata shape during rollout. */
  readonly submissions: number | null
}

export interface RankMeta {
  readonly presetId: 'mxdc'
  readonly supportsAllLevelBuckets?: boolean
  readonly supportsSearch?: boolean
  readonly searchMaxLength?: number
  readonly supportsMapDetails?: boolean
  readonly mapDetailDropCategories?: readonly RankDropCategory[]
  readonly levelBuckets: readonly number[]
  readonly jobs: readonly RankMetaJob[]
  readonly sources: readonly RankSource[]
  readonly minimumUsers: number
  readonly minimumTrustedDurationMs: number
  readonly minimumUserSampleDurationMs: number
  readonly lastUpdatedAt: number | null
}

export type RankDropCategory = 'equipment' | 'scroll'

export interface RankTooltip {
  readonly title: string
  readonly lines: readonly string[]
}

export interface RankMapInfo {
  readonly id: number
  readonly name: string
  readonly street: string
  readonly image: string
  readonly monsterCount: number
  readonly spawnCount: number
  readonly minLevel: number
  readonly maxLevel: number
}

export interface RankMonster {
  readonly id: number
  readonly name: string
  readonly level: number
  readonly exp: number
  readonly spawnCount: number
  readonly icon: string
  readonly detailUrl: string
  readonly tooltip: RankTooltip
}

export interface RankDrop {
  readonly id: number
  readonly name: string
  readonly category: RankDropCategory
  readonly icon: string
  readonly detailUrl: string
  readonly tooltip: RankTooltip
}

export interface RankMapDetail {
  readonly presetId: 'mxdc'
  readonly map: RankMapInfo
  readonly monsters: readonly RankMonster[]
  readonly drops: readonly RankDrop[]
  readonly dropCategories: readonly RankDropCategory[]
}

export type RankSyncStatus = 'idle' | 'syncing' | 'waiting-network' | 'configuration-error'

export interface RankSyncState {
  readonly status: RankSyncStatus
  readonly pendingCount: number
  readonly lastSucceededAt: number | null
  readonly lastErrorAt: number | null
}

export interface ExpPoint {
  readonly admittedTotal?: number
  readonly admissionEpoch?: number
  readonly percentagePrecision?: number
  readonly level: number
  readonly absolute: number
  readonly ratio: number
  /** Null only for a zero-percent endpoint before this level has enough samples. */
  readonly requiredExp: number | null
  readonly requiredExpApproximate: boolean
}

export const SESSION_JOURNEY_VERSION = 3

export function isSupportedJourneyVersion(version: unknown): version is number {
  return version === 1 || version === 2 || version === SESSION_JOURNEY_VERSION
}

export interface ExpJourneyTransition {
  readonly id?: string
  readonly revision?: number
  readonly kind?: 'level-up' | 'loss' | 'correction'
  readonly status: 'pending' | 'confirmed' | 'discarded' | 'unavailable'
  readonly fromSourceAt: number
  readonly toSourceAt: number
  /** Confirmed net earnings at a buffered sample equal its absolute EXP plus this offset. */
  readonly netOffset?: number
  readonly correctionFromSourceAt?: number
  readonly level?: number
  readonly levelUp?: { readonly from: number; readonly to: number }
}

export interface ExpDisplayUpdate {
  readonly netGain: number | null
  /** Unpublished endpoint, only for buffering the journey until the change resolves. */
  readonly pendingNetGain?: number | null
  readonly sourceAt: number | null
  readonly point: ExpPoint | null
  readonly status: 'ready' | 'stale' | 'pending-transition' | 'unresolved-level' | 'unavailable'
  readonly approximate: boolean
  readonly continuity: 'baseline' | 'contiguous' | 'discontinuous'
  readonly levelUp?: { readonly from: number; readonly to: number }
  readonly journeyTransition?: ExpJourneyTransition
}

export type SessionJourneyContinuity = 'baseline' | 'contiguous' | 'discontinuous' | 'rebaseline'

export interface SessionJourneyPoint {
  readonly activeDurationMs: number
  readonly capturedAt: number
  readonly expGained: number
  readonly level: number
  readonly expAbsolute: number
  readonly expRatio: number
  readonly mapName: string | null
  readonly approximate: boolean
  readonly continuity: SessionJourneyContinuity
  readonly anchor: boolean
}

export type SessionJourneyEventType =
  | 'level-up'
  | 'map-change'
  | 'pause'
  | 'resume'
  | 'capture-interrupted'
  | 'capture-restored'
  | 'rebaseline'

export interface SessionJourneyEvent {
  readonly type: SessionJourneyEventType
  readonly activeDurationMs: number
  readonly capturedAt: number
  readonly fromLevel?: number
  readonly toLevel?: number
  readonly previousMapName?: string
  readonly mapName?: string
}

export interface SessionJourney {
  readonly sessionId: string
  readonly version: number
  readonly startedAt: number
  readonly updatedAt: number
  readonly bucketSizeMs: number
  readonly points: readonly SessionJourneyPoint[]
  readonly events: readonly SessionJourneyEvent[]
  readonly pendingExpSince?: number
}
